import { ReactNode } from 'react';

import classNames from 'classnames';
import { clamp } from 'lodash';

import { Typography } from '../Typography';

export interface ProgressBarProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  min?: number;
  max?: number;
  value?: number;
  variant?: 'primary' | 'secondary';
  size?: 's' | 'm';
  progress?: 'progress' | 'over';
}

export const ProgressBar = ({
  value = 0,
  min = 0,
  max = 100,
  variant = 'primary',
  size = 's',
  progress = 'progress',
  title,
  subtitle,
}: ProgressBarProps) => {
  const clampedValue = clamp(value, min, max);
  const scaleX = (100 / (max - min)) * (clampedValue - min);
  const hasTitle = title !== undefined && title !== null;
  const hasSubtitle = subtitle !== undefined && subtitle !== null;

  const showHeader = hasTitle || hasSubtitle;

  return (
    <div className="flex flex-col gap-2 w-full">
      {showHeader && (
        <div className="flex justify-between gap-2">
          <Typography variant="caption-s-strong" color="primary">
            {title}
          </Typography>
          <Typography variant="caption-s" color="tertiary">
            {subtitle}
          </Typography>
        </div>
      )}
      <div className={classNames('progress-bar', `size-${size}`, progress, variant)}>
        <div
          role="progressbar"
          className={classNames('progress-bar-indicator', variant)}
          aria-valuenow={value}
          aria-valuemin={min}
          aria-valuemax={max}
          style={{
            transform: `scaleX(${scaleX}%)`,
            transition: 'transform 1s ease-out',
          }}
        />
      </div>
    </div>
  );
};
